import { createContext, useContext, useState, useCallback } from 'react'
import type { ReactNode } from 'react'

interface UserPreferencesContextValue {
  // Location filter (for multi-location users)
  // 'all' means show all locations, otherwise array of selected location IDs
  selectedLocationIds: string[] | 'all'
  setSelectedLocationIds: (ids: string[] | 'all') => void

  // Hide "Coming Soon" navigation items
  hideComingSoon: boolean
  setHideComingSoon: (hide: boolean) => void
  toggleHideComingSoon: () => void

  // Debug mode - enables debug tools and extra console logging
  debugMode: boolean
  setDebugMode: (debug: boolean) => void
  toggleDebugMode: () => void
}

const STORAGE_KEY_LOCATION_FILTER = 'planmax_location_filter'
const STORAGE_KEY_HIDE_COMING_SOON = 'planmax_hide_coming_soon'
const STORAGE_KEY_DEBUG_MODE = 'planmax_debug_mode'

const UserPreferencesContext = createContext<UserPreferencesContextValue | null>(null)

interface UserPreferencesProviderProps {
  children: ReactNode
}

export function UserPreferencesProvider({ children }: UserPreferencesProviderProps) {
  // Initialize location filter from localStorage or default to 'all'
  const [selectedLocationIds, setSelectedLocationIdsState] = useState<string[] | 'all'>(() => {
    if (typeof window === 'undefined') return 'all'
    const stored = localStorage.getItem(STORAGE_KEY_LOCATION_FILTER)
    if (!stored || stored === 'all') return 'all'
    try {
      const parsed = JSON.parse(stored)
      return Array.isArray(parsed) ? parsed : 'all'
    } catch {
      return 'all'
    }
  })

  // Initialize hideComingSoon from localStorage or default to false
  const [hideComingSoon, setHideComingSoonState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    const stored = localStorage.getItem(STORAGE_KEY_HIDE_COMING_SOON)
    return stored === 'true'
  })

  // Initialize debugMode from localStorage or default to false
  const [debugMode, setDebugModeState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    const stored = localStorage.getItem(STORAGE_KEY_DEBUG_MODE)
    return stored === 'true'
  })

  // Persist location filter to localStorage
  const setSelectedLocationIds = useCallback((ids: string[] | 'all') => {
    setSelectedLocationIdsState(ids)
    localStorage.setItem(
      STORAGE_KEY_LOCATION_FILTER,
      ids === 'all' ? 'all' : JSON.stringify(ids)
    )
  }, [])

  // Persist hideComingSoon to localStorage
  const setHideComingSoon = useCallback((hide: boolean) => {
    setHideComingSoonState(hide)
    localStorage.setItem(STORAGE_KEY_HIDE_COMING_SOON, hide.toString())
  }, [])

  // Toggle hideComingSoon
  const toggleHideComingSoon = useCallback(() => {
    setHideComingSoon(!hideComingSoon)
  }, [hideComingSoon, setHideComingSoon])

  // Persist debugMode to localStorage
  const setDebugMode = useCallback((debug: boolean) => {
    setDebugModeState(debug)
    localStorage.setItem(STORAGE_KEY_DEBUG_MODE, debug.toString())
  }, [])

  // Toggle debugMode
  const toggleDebugMode = useCallback(() => {
    setDebugMode(!debugMode)
  }, [debugMode, setDebugMode])

  const value: UserPreferencesContextValue = {
    selectedLocationIds,
    setSelectedLocationIds,
    hideComingSoon,
    setHideComingSoon,
    toggleHideComingSoon,
    debugMode,
    setDebugMode,
    toggleDebugMode,
  }

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  )
}

export function useUserPreferences(): UserPreferencesContextValue {
  const context = useContext(UserPreferencesContext)
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider')
  }
  return context
}
